import React from 'react';
import { observer } from 'mobx-react-lite';

import { ClickAwayListener, IconButton } from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/Close';

import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';
import EntitiesFormField from 'containers/EntitiesFormSubmission/components/EntitiesFormField';

import { EntityModules, ProtectorType } from 'core/constants';
import { AnswerTypes, JSONSchemaType } from 'utils/api/modules/incidents/incidents.types';
import { EntityValueType, EntityEnumsOptions } from 'utils/api/modules/entities/entities.types';

import { MetadataRow, MetadataLabel, EditableValue, EditContainer, EditActions } from './styles';

type InlineEditableFieldProps = {
  fieldKey: string;
  schema: JSONSchemaType;
  value: EntityValueType;
  canEdit: boolean;
  onSave: (fieldKey: string, value: EntityValueType) => Promise<boolean>;
};

const isDropdownType = (type: string): boolean =>
  type === ProtectorType.SingleChoice || type === ProtectorType.MultiChoice;

// Transform {key, value}[] to format expected by CustomFieldDisplayValue
const getDisplayValue = (value: EntityValueType, protectorType: string): AnswerTypes => {
  if (!isDropdownType(protectorType)) {
    return value as AnswerTypes;
  }

  const dropdownValue = value as EntityEnumsOptions[] | null;
  if (!dropdownValue || dropdownValue.length === 0) {
    return null;
  }

  if (protectorType === ProtectorType.MultiChoice) {
    return dropdownValue.map((v) => v.value);
  }
  return dropdownValue[0]?.value || null;
};

const InlineEditableField: React.FC<InlineEditableFieldProps> = observer(
  ({ fieldKey, schema, value, canEdit, onSave }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [localValue, setLocalValue] = React.useState<EntityValueType>(value);
    const [isSaving, setIsSaving] = React.useState(false);

    const isDropdown = isDropdownType(schema.protectorType);

    React.useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleSave = async (): Promise<void> => {
      if (localValue !== value) {
        setIsSaving(true);
        const success = await onSave(fieldKey, localValue);
        setIsSaving(false);
        if (success) {
          setIsEditing(false);
        }
      } else {
        setIsEditing(false);
      }
    };

    const handleDiscard = (): void => {
      setLocalValue(value);
      setIsEditing(false);
    };

    const handleChange = (newValue: AnswerTypes): void => {
      setLocalValue(newValue as EntityValueType);
    };

    // For dropdown fields, convert {key, value}[] to string[] of keys for the form field
    const getFormFieldValue = (): EntityValueType => {
      if (isDropdown && Array.isArray(localValue)) {
        return (localValue as EntityEnumsOptions[]).map((v) => v.key);
      }
      return localValue;
    };

    // Get enum items - they're already in {key, value, active} format
    const getEnumItems = (): { key: string; value: string }[] => {
      if (!schema.enum) return [];
      return (schema.enum as unknown as EntityEnumsOptions[]).filter((e) => e.active !== false);
    };

    const displayValue = getDisplayValue(value, schema.protectorType);

    if (schema.readonly) {
      return (
        <MetadataRow>
          <MetadataLabel>{schema.name}</MetadataLabel>
          <EditableValue $editable={false}>
            <CustomFieldDisplayValue
              value={displayValue}
              type={schema.protectorType as ProtectorType}
              schema={schema}
              lookupType={schema.lookupType}
            />
          </EditableValue>
        </MetadataRow>
      );
    }

    return (
      <MetadataRow>
        <MetadataLabel>{schema.name}</MetadataLabel>
        {isEditing ? (
          <ClickAwayListener onClickAway={handleDiscard}>
            <EditContainer>
              <EntitiesFormField
                type={schema.protectorType as ProtectorType}
                value={getFormFieldValue() || ''}
                onChange={handleChange}
                description={schema.name || ''}
                items={getEnumItems()}
                lookupType={schema.lookupType as EntityModules}
                fieldKey={fieldKey}
                requiredNotFilled={false}
              />
              <EditActions>
                <IconButton onClick={handleDiscard} size="small" disabled={isSaving}>
                  <CloseIcon sx={{ width: '20px', height: '20px', color: 'action.active' }} />
                </IconButton>
                <IconButton onClick={handleSave} size="small" disabled={isSaving}>
                  <CheckOutlinedIcon sx={{ width: '20px', height: '20px', color: 'action.active' }} />
                </IconButton>
              </EditActions>
            </EditContainer>
          </ClickAwayListener>
        ) : (
          <EditableValue $editable={canEdit} onClick={() => canEdit && setIsEditing(true)}>
            <CustomFieldDisplayValue
              value={displayValue}
              type={schema.protectorType as ProtectorType}
              schema={schema}
              lookupType={schema.lookupType}
            />
          </EditableValue>
        )}
      </MetadataRow>
    );
  }
);

export default InlineEditableField;
